import type { CategoryContent } from './row.js';

export const GERMAN_HISTORY: CategoryContent = {
  bscba: [
    [
      'Which Prussian monarch is most famous for military reform and territorial expansion in the 18th century?',
      [
        'Frederick the Great',
        'Frederick William I',
        'Charles XII of Sweden',
        'Joseph II of Austria',
      ],
      0,
      'Frederick the Great (r. 1740–1786) transformed Prussia through military innovation and territorial conquest, making it a great power.',
    ],
    [
      'What approach did Otto von Bismarck use to achieve German unification?',
      [
        'Pragmatic power politics over ideology',
        'Democratic reform and constitutional compromise',
        'Alliance with socialist movements',
        'Religious authority and papal support',
      ],
      0,
      'Bismarck\'s Realpolitik used military force and diplomacy—not ideology—to unify Germany under Prussian dominance.',
    ],
    [
      'Which conflict in 1870–1871 led directly to German unification?',
      [
        'Franco-Prussian War',
        'The Austro-Prussian War',
        'The Crimean War',
        'The Napoleonic Wars',
      ],
      0,
      'German victory in the Franco-Prussian War enabled the proclamation of the German Empire on 18 January 1871.',
    ],
    [
      'Who became the first German Emperor (Kaiser) in 1871?',
      [
        'William I',
        'William II',
        'Otto von Bismarck',
        'Frederick William IV',
      ],
      0,
      'William I, King of Prussia, was proclaimed German Emperor. Bismarck became Chancellor under his authority.',
    ],
    [
      'What major treaty imposed harsh penalties on France after German unification?',
      [
        'The Treaty of Frankfurt (1871)',
        'The Treaty of Versailles (1919)',
        'The Treaty of Vienna (1815)',
        'The Peace of Westphalia (1648)',
      ],
      0,
      'The 1871 Treaty of Frankfurt required France to pay 5 billion francs and cede Alsace-Lorraine to Germany.',
    ],
    [
      'What was the primary cause of instability in the early Weimar Republic?',
      [
        'Resentment of the Treaty of Versailles',
        'Successful industrialization abroad',
        'Unified political consensus among parties',
        'Universal support from neighboring nations',
      ],
      0,
      'The Treaty of Versailles imposed reparations, territorial losses, and military limits that fueled Weimar resentment and instability.',
    ],
    [
      'In which year did hyperinflation devastate the German economy?',
      [
        '1923',
        '1929',
        '1933',
        '1919',
      ],
      0,
      'German hyperinflation peaked in 1923, destroying savings and deepening social and political crisis in Weimar.',
    ],
    [
      'What was the structure of the Weimar government?',
      [
        'A federal parliamentary republic',
        'An absolute monarchy',
        'A unitary dictatorship',
        'A confederation with no central power',
      ],
      0,
      'Weimar was a federal parliamentary democracy with an elected president, chancellor, and bicameral legislature.',
    ],
    [
      'Which global event destabilized Weimar in 1929?',
      [
        'The 1929 stock market crash',
        'Japanese invasion of Manchuria',
        'Italian fascist expansion',
        'Soviet agricultural collectivization',
      ],
      0,
      'The 1929 Wall Street crash triggered the Great Depression, devastating Weimar\'s already fragile economy.',
    ],
    [
      'What marked the beginning of Nazi rule in Germany?',
      [
        'Hitler\'s appointment',
        'The Treaty of Versailles in 1919',
        'The hyperinflation of 1923',
        'The Munich Putsch of 1923',
      ],
      0,
      'Hitler was appointed Chancellor on 30 January 1933. The Enabling Act in March gave him dictatorial power.',
    ],
    [
      'What did the 1935 Nuremberg Laws establish?',
      [
        'Revoked Jewish citizenship',
        'Military alliances with Italy and Japan',
        'Territorial expansion into Austria',
        'A democratic constitutional framework',
      ],
      0,
      'The Nuremberg Laws stripped Jews of German citizenship and prohibited intermarriage as legal persecution began.',
    ],
    [
      'What core ideology defined the Nazi regime?',
      [
        'Fascist antisemitism',
        'Liberal democracy',
        'Marxist communism',
        'Constitutional monarchy',
      ],
      0,
      'Nazism fused fascism, extreme antisemitism, and pseudoscientific racial ideology centered on Aryan supremacy.',
    ],
    [
      'What resulted directly from Germany\'s 1945 defeat in World War II?',
      [
        'Four Allied occupation zones',
        'Restoration of the Prussian monarchy',
        'German eastward expansion',
        'Unification under a German-led superstate',
      ],
      0,
      'After 1945, Allied forces divided Germany into American, British, Soviet, and French occupation zones.',
    ],
    [
      'Which structure symbolized Cold War division between East and West Germany?',
      [
        'The Berlin Wall',
        'The Treaty of Versailles',
        'The Reformation',
        'The Congress of Vienna',
      ],
      0,
      'The Berlin Wall, built in 1961, divided Berlin and represented Cold War partition for 28 years until 1989.',
    ],
    [
      'What caused the fall of the Berlin Wall in 1989?',
      [
        'Collapse of Soviet power',
        'A military invasion from the West',
        'An internal West German revolution',
        'Economic success of East Germany',
      ],
      0,
      'Growing dissent in East Germany and the Soviet Union\'s refusal to use force led to the Wall\'s fall in November 1989.',
    ],
  ],
  msc: [
    [
      'What name describes the loose association of 39 German states from 1815 to 1866?',
      [
        'German Confederation',
        'The Zollverein',
        'The Holy Roman Empire',
        'The Prussian League',
      ],
      0,
      'The German Confederation (1815–1866) was a loose association of sovereign German states under Austrian presidency.',
    ],
    [
      'Which Prussian-led customs union excluded Austria and unified German tariffs?',
      [
        'The Zollverein',
        'The Hanseatic League',
        'The Rheinischer Bund',
        'The Germanic Trade Union',
      ],
      0,
      'The Zollverein (1834) unified German tariffs while excluding Austria, advancing Prussian economic and political dominance.',
    ],
    [
      'What was the Frankfurt Parliament of 1848–1849 attempting to accomplish?',
      [
        'A unified German nation-state',
        'Enforcing Austrian dominance over Germany',
        'Negotiating trade treaties with France',
        'Addressing theological disputes from the Reformation',
      ],
      0,
      'The Frankfurt Parliament was a liberal attempt to unify Germany constitutionally, but ultimately failed to achieve lasting change.',
    ],
    [
      'What principle drove Bismarck\'s statement about German unification being achieved through "blood and iron"?',
      [
        'Military force, not negotiation',
        'A purely defensive military doctrine',
        'Economic sanctions as a primary tool',
        'Diplomatic persuasion and negotiation',
      ],
      0,
      '"Blood and iron" (Blut und Eisen) expressed Bismarck\'s belief that German unification required military victory.',
    ],
    [
      'Which conflict of 1866 decisively removed Austria from German affairs?',
      [
        'The Austro-Prussian War',
        'The Napoleonic Wars',
        'The Thirty Years\' War',
        'The War of Spanish Succession',
      ],
      0,
      'The 1866 Austro-Prussian War decisively defeated Austria and established Prussian hegemony in German politics.',
    ],
    [
      'What imposed reparations and territorial losses on France after the Franco-Prussian War?',
      [
        'The Treaty of Frankfurt (1871)',
        'The Treaty of Paris (1815)',
        'The Treaty of Westphalia (1648)',
        'The Treaty of Vienna (1815)',
      ],
      0,
      'The Treaty of Frankfurt (1871) imposed 5 billion gold francs reparations and ceded Alsace-Lorraine to Germany.',
    ],
    [
      'What term describes Bismarck\'s conservative coalition uniting landowners and industrialists?',
      [
        'Sammlungspolitik',
        'Kulturkampf (cultural struggle)',
        'Weltpolitik (world politics)',
        'Lebensraum (living space)',
      ],
      0,
      'Sammlungspolitik united agrarian Junkers and industrialists to preserve elite power against rising socialist movements.',
    ],
    [
      'What was the Spartacist League\'s attempt to seize power during early Weimar?',
      [
        'A communist uprising crushed by Freikorps',
        'A successful social democratic reform movement',
        'An alliance with Nazi forces',
        'A peaceful peasant movement',
      ],
      0,
      'The 1919 Spartacist Uprising, led by Rosa Luxemburg and Karl Liebknecht, was brutally suppressed.',
    ],
    [
      'What did the 1924 Dawes Plan accomplish?',
      [
        'Restructured reparations plan',
        'Demanded complete German disarmament',
        'Proposed Soviet-German cooperation',
        'Authorized French occupation of the Ruhr',
      ],
      0,
      'The Dawes Plan (1924) reorganized reparations and provided American loans, temporarily stabilizing Weimar.',
    ],
    [
      'What characterized the "Golden Twenties" period (ca. 1924–1929) in Weimar?',
      [
        'Cultural and economic revival',
        'Complete economic collapse',
        'Rejection of all foreign investment',
        'Communist industrialization success',
      ],
      0,
      'The "Golden Twenties" saw cultural flourishing and economic recovery before the Great Depression.',
    ],
    [
      'Which legal mechanism granted Hitler emergency legislative powers in 1933?',
      [
        'The Enabling Act (1933)',
        'A military coup',
        'A nationwide referendum',
        'A presidential decree alone',
      ],
      0,
      'The Enabling Act allowed Hitler to enact laws without Reichstag approval, establishing dictatorship.',
    ],
    [
      'What pseudoscientific foundation justified Nazi race laws and persecution?',
      [
        'False Aryan racial superiority theories',
        'A doctrine derived from Christian theology',
        'Economic competition with Jewish businesses',
        'Legitimate biological science accepted worldwide',
      ],
      0,
      'Nazi ideology misappropriated racial pseudoscience to justify genocide and persecution.',
    ],
    [
      'What term encompasses the Nazi genocide against Jews and other targeted groups?',
      [
        'The Holocaust',
        'Kristallnacht',
        'The Final Solution',
        'The Blitzkrieg',
      ],
      0,
      'The Holocaust was the systematic genocide of six million Jews and millions of others by the Nazi regime.',
    ],
    [
      'How did the Marshall Plan influence the recovery of West Germany?',
      [
        'American economic aid (1948-1952)',
        'Military occupation lasting indefinitely',
        'Reparations payments flowing to the Soviet Union',
        'Forced Soviet-style agricultural collectivization',
      ],
      0,
      'The Marshall Plan provided crucial American aid for West German economic recovery and democratic consolidation.',
    ],
    [
      'What ideological division characterized Cold War Germany?',
      [
        'Liberal capitalist West, communist East',
        'Both were monarchies seeking restoration',
        'Both maintained equal internal democracy',
        'East Germany was capitalist, West Germany was socialist',
      ],
      0,
      'East Germany (GDR) was communist under Soviet control; West Germany (FRG) was a capitalist parliamentary democracy.',
    ],
  ],
  phd: [
    [
      'How did the Landwehr reforms (1807–1815) transform Prussian military organization?',
      [
        'Combining regular army with militia reserves',
        'By relying exclusively on mercenary forces',
        'By prioritizing naval over land forces',
        'By abolishing standing armies for feudal levies',
      ],
      0,
      'Scharnhorst\'s Landwehr reforms created universal conscription, merging professional and reserve forces.',
    ],
    [
      'How did Zollverein tariff structure specifically isolate Austria while integrating German states?',
      [
        'High external tariffs, free internal trade',
        'Equal tariffs applied universally without discrimination',
        'Subsidies exclusively favored agrarian interests',
        'Complete elimination of all inter-state tariffs',
      ],
      0,
      'Zollverein tariffs economically integrated German states while structurally excluding Austrian interests.',
    ],
    [
      'What historiographical debate addressed whether German unification resulted from elite action or popular nationalism?',
      [
        'Unification "from above" versus "from below"',
        'Whether Prussia or Austria should have led unification',
        'Whether industrialization or agriculture drove development',
        'Whether German culture predated the nation-state',
      ],
      0,
      'Historians debate whether Bismarck\'s elite machinations or grassroots nationalism drove unification.',
    ],
    [
      'What underlying state-society tensions did Kulturkampf (1871–1878) expose in the new Empire?',
      [
        'Nationalism versus Catholic papal loyalty',
        'Purely theological doctrinal disputes',
        'A Catholic campaign for political dominance',
        'An anti-Jewish alliance between Protestants and Catholics',
      ],
      0,
      'Kulturkampf pitted Bismarck\'s centralized nation-state against Catholic institutional independence.',
    ],
    [
      'How did Sammlungspolitik represent a strategic shift in maintaining elite dominance?',
      [
        'Uniting agrarian and industrial elites',
        'By implementing democratic electoral reforms',
        'By allying conservative factions with socialists',
        'By pursuing militaristic party organization',
      ],
      0,
      'Sammlungspolitik consolidated conservative elites to resist social democracy and universal suffrage.',
    ],
    [
      'How did German industrial development under the Second Reich exceed Britain\'s by 1914?',
      [
        'Germany surpassed Britain in heavy industry',
        'Britain remained perpetually dominant in all sectors',
        'Germany focused exclusively on agriculture',
        'Industrial development was negligible in both nations',
      ],
      0,
      'German heavy industry surpassed Britain; German firms formed syndicates and cartels unlike British competition.',
    ],
    [
      'What does the historiographical concept of "vetoization" explain about Weimar?',
      [
        'Entrenched elites blocked democratic solutions',
        'The process of successful democratization',
        'How plural veto powers protected individual liberties',
        'A strengthening of socialist electoral power',
      ],
      0,
      'Vetoization theory describes elite deadlock that prevented legislative action, creating opportunities for Nazi seizure of power.',
    ],
    [
      'How did the "stab-in-the-back myth" (Dolchstoßlegende) function ideologically in Weimar politics?',
      [
        'A false narrative of civilian betrayal',
        'An accurate historical assessment of military conditions in 1918',
        'A Socialist propaganda creation',
        'An Austrian political claim against Germany',
      ],
      0,
      'The Dolchstoßlegende falsely claimed home betrayal rather than military defeat, providing Nazi ideology crucial mobilization.',
    ],
    [
      'Which constitutional weaknesses in Weimar enabled Hitler\'s consolidation of power?',
      [
        'Article 48 powers and Reichstag fragmentation',
        'A system too restrictive for any executive action',
        'Excessive constitutional protection of civil liberties',
        'A prohibition on political party formation',
      ],
      0,
      'Article 48 allowed presidential emergency decrees; proportional representation fragmented stable coalitions.',
    ],
    [
      'What did Nazi "Gleichschaltung" accomplish in restructuring civil society?',
      [
        'Total Nazi control of all organizations',
        'Decentralization of power to local communities',
        'Strengthening the Reichstag\'s legislative authority',
        'Abolishing political parties in favor of monarchy',
      ],
      0,
      'Gleichschaltung synchronized all institutions under Nazi control, eliminating civil society independence.',
    ],
    [
      'How did SS ideology differ from Wehrmacht professional military doctrine?',
      [
        'The SS embraced Nazi racial ideology',
        'They were identical in purpose and actions',
        'The Wehrmacht rejected all Nazi ideology',
        'The SS was exclusively a labor organization',
      ],
      0,
      'The SS executed Nazi racial crimes; the Wehrmacht\'s relationship to atrocities remains contested among historians.',
    ],
    [
      'How did the "Lebensraum" concept justify Nazi expansion eastward?',
      [
        'German territorial colonization',
        'A peaceful cultural exchange program',
        'A trade agreement with Eastern nations',
        'A defensive treaty with Poland',
      ],
      0,
      'Lebensraum justified eastward conquest and genocide as necessary for German territorial and racial dominance.',
    ],
    [
      'How did the compromised Nazi-era backgrounds of West German elites affect early Federal Republic politics?',
      [
        'Incomplete denazification enabled elite continuity',
        'West Germany completely purged all Nazi-era officials',
        'The Federal Republic was established with perfect democratic purity',
        'Nazi-era personnel had no involvement in postwar governance',
      ],
      0,
      'West German elites often retained Nazi-era records; incomplete denazification preserved traditional power structures.',
    ],
    [
      'How do historians debate whether Cold War division was aberration or revealing deeper fractures?',
      [
        'Division as anomaly versus longstanding divisions',
        'All historians agree on a single interpretation',
        'The debate is purely ideological without scholarly substance',
        'Historical evidence clearly resolves the question unambiguously',
      ],
      0,
      'Scholars disagree whether post-1945 division was temporary aberration or revealed deeper German divisions.',
    ],
    [
      'What institutional factors enabled West Germany\'s "Economic Miracle" (Wirtschaftswunder)?',
      [
        'Marshall Aid and currency reform',
        'Central Soviet planning',
        'Agricultural focus excluding industrial development',
        'Complete rejection of foreign investment and aid',
      ],
      0,
      'The Wirtschaftswunder combined Marshall Aid, currency reform, and Ludwig Erhard\'s social market model.',
    ],
  ],
  professor: [
    [
      'How does recent scholarship reframe the Frankfurt Parliament\'s failure in light of transnational comparative analysis?',
      [
        'Constitutional liberalism faced obstacles across Europe',
        'By proving German liberals were incompetent compared to other nations',
        'By confirming Germany was destined for authoritarian rule',
        'By endorsing Treitschke\'s nationalist historiography',
      ],
      0,
      'Contemporary historians use cross-national comparison to challenge German Sonderweg claims, revealing comparable obstacles.',
    ],
    [
      'How does Christopher Clark\'s reassessment of the July Crisis situate German agency in World War I origins?',
      [
        'Within a multipolar European Great Power system',
        'By absolving Germany entirely of responsibility',
        'By claiming war was inevitable regardless of policy',
        'By promoting an earlier Sonderweg thesis',
      ],
      0,
      'Clark contextualizes German decisions within competing Great Power interests, challenging monocausal aggression narratives.',
    ],
    [
      'What historiographical concept explains Weimar collapse through elite deadlock and institutional fragmentation?',
      [
        'Vetoization: entrenched elites blocked solutions',
        'Democratic strengthening through plural veto powers',
        'Weimar\'s successful plural protection of individual liberties',
        'Increasing power concentration in socialist parties',
      ],
      0,
      'Vetoization describes how fragmented elites\' blocking of legislative action created opportunities for Nazi emergence.',
    ],
    [
      'How did the Deutsche Arbeitsfront (Nazi Labor Front) restructure labor relations distinctly from Weimar?',
      [
        'Corporatist control of labor',
        'By strengthening independent trade unions',
        'By reducing state involvement in labor matters',
        'By adopting Soviet-style communist labor organization',
      ],
      0,
      'The DAF replaced independent unions with Nazi-controlled corporatism: wage control and ideological integration.',
    ],
    [
      'What does Raul Hilberg\'s institutional analysis emphasize about the Holocaust\'s bureaucratic character?',
      [
        'Cumulative radicalization and bureaucratic momentum',
        'That genocide was spontaneous mob violence without organization',
        'That technology played no role in atrocities',
        'That it resulted solely from one individual\'s decision',
      ],
      0,
      'Hilberg traced how Holocaust machinery emerged through bureaucratic interaction and incremental institutional radicalization.',
    ],
    [
      'How do debates on "ordinary perpetrators" complicate functionalist interpretations of Nazi crimes?',
      [
        'Ideological commitment coexisting with bureaucratic roles',
        'By proving all perpetrators were committed ideologues',
        'By absolving institutional actors of responsibility',
        'By returning exclusively to intentionalist Nazi ideology explanations',
      ],
      0,
      'Recent scholarship shows perpetrators acted from ideology, careerism, and conformity intertwined, not purely bureaucratic roles.',
    ],
    [
      'What historiographical significance does Eckart Kehr\'s "battle fleet theory" hold for Imperial German politics?',
      [
        'Naval expansion reinforced elite coalitions',
        'Purely military technical analysis disconnected from politics',
        'That naval power is irrelevant to political development',
        'A theory originating in the Nazi period',
      ],
      0,
      'Kehr linked German battleship expansion to elite coalitions: imperialism distracted from domestic democratic reform.',
    ],
    [
      'How do scholars now interpret East German "state socialism" regarding legitimacy and surveillance coexistence?',
      [
        'Surveillance mixed with negotiated compliance',
        'As entirely voluntary and popular',
        'As having no security apparatus',
        'As identical to West German governance structures',
      ],
      0,
      'Recent scholarship emphasizes GDR coexistence of surveillance with petty negotiation and performance.',
    ],
    [
      'What historiographical innovation does Timothy Snyder\'s "bloodlands" concept introduce to Nazi-Soviet comparison?',
      [
        'Centering Eastern European civilian victims',
        'By exonerating one side entirely',
        'By claiming Eastern Europe was historically irrelevant',
        'By ignoring ideology in favor of pure geography',
      ],
      0,
      'Snyder recenters Eastern European victims in contested zones where Nazi and Soviet violence collided.',
    ],
    [
      'How did monetary sovereignty and the Deutsche Mark function symbolically in West German post-war identity?',
      [
        'The DM symbolized economic autonomy',
        'Currency was irrelevant to national identity',
        'The DM strengthened German-Soviet relations',
        'West Germany lacked independent monetary policy',
      ],
      0,
      'The Deutsche Mark symbolized West German recovery and autonomy; currency stability became central to identity formation.',
    ],
    [
      'What historiographical debates characterized the 1980s Historikerstreit (historians\' dispute)?',
      [
        'Intentionalists versus functionalists debate',
        'Universal scholarly agreement on all interpretations',
        'Purely ideological disputes without scholarly rigor',
        'A dispute among non-specialist commentators',
      ],
      0,
      'The Historikerstreit pitted scholars on causation, continuity narratives, and ethical narration of Nazi genocide.',
    ],
    [
      'How does Konrad Jarausch\'s "German Dilemma" framework interpret longue durée tensions in German governance?',
      [
        'Tension between pluralism and centralization',
        'As proof of inherent German inferiority',
        'As uniquely German without European parallel',
        'As permanently resolved by German unification in 1871',
      ],
      0,
      'Jarausch argues German unification efforts repeatedly privileged elite centralization over democratic pluralism.',
    ],
    [
      'How does Dagmar Herzog\'s work on sexuality complicate monolithic interpretations of Nazi control?',
      [
        'Repression and permission of sexuality coexisted',
        'By claiming Nazis entirely ignored sexuality',
        'By asserting sexual freedom flourished under Nazism',
        'By proving Nazi repression was completely ineffective',
      ],
      0,
      'Herzog reveals Nazi policies contradictorily repressed homosexuals while permitting heterosexual conduct for racial reproduction.',
    ],
    [
      'How have scholars reassessed West Germany\'s 1968 student movements regarding generational reckoning with history?',
      [
        'Youth confronting parental Nazi-era complicity',
        'As entirely imported from foreign movements',
        'As completely disconnected from historical concerns',
        'As supporting existing institutional authorities',
      ],
      0,
      '1968 West German students confronted parental silence on Nazi complicity and Cold War militarization.',
    ],
    [
      'What does David Calleo\'s "German Problem" framework suggest about European integration\'s role?',
      [
        'Integrating German power within European institutions',
        'That Germany should openly dominate Europe',
        'That European integration is irrelevant to German power',
        'That Germany is naturally peaceful without institutional constraints',
      ],
      0,
      'Calleo argues European institutions constrain German power while enabling integration, solving the "German Problem."',
    ],
  ],
};
